import React from "react";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { CalendarDots, Heartbeat } from "@phosphor-icons/react";

import { CUSTOM_COLORS } from "constants/colors";
import ModuleHeader from "../ModuleHeader";
import { ModuleContainer } from "../styles";
import BilledHoursChart from "./BilledHoursChart";
import Bullet from "./Bullet";
import EmptySummary from "./EmptySummary";

const { purple, orange } = CUSTOM_COLORS;

const getMinimumHours = (employmentType) => {
  const minimumHours = {
    W2_1_FTE: 28,
    W2_75_FTE: 21,
    W2_5_FTE: 14,
    Clinical_Assessor: 32,
  };

  return minimumHours[employmentType] || null;
};

const SummaryWeekLegend = () => {
  return (
    <Stack columnGap={7} mt={{ xs: 5, sm: 7 }} direction={{ xs: "column", sm: "row" }}>
      <Box>
        <Typography component="p" variant="caption" color="text.secondary" align="center">
          <Bullet color={purple[800]} />
          Billed hours
        </Typography>
      </Box>

      <Box>
        <Typography component="p" variant="caption" color="text.secondary" align="center">
          <Bullet color={purple[200]} />
          Remaining authorized hours
        </Typography>
      </Box>
    </Stack>
  );
};
/**
 * @component
 * @description `SummaryWeek` component displays delivered hours (completed services from their AN calendar)
 * over total authorized hours across all their assigned clients during the current week.
 *
 * @param {Object} props - The component props.
 * @param {number} props.authorizedHours - The number of authorized hours.
 * @param {string} props.employmentType - The employment type of the clinician.
 * @param {number} props.deliveredHours - The number of delivered hours.
 * @param {number} props.scheduledHours - The number of scheduled hours.
 * @param {boolean} props.isSmallScreen - Whether the screen is small.
 * @returns {React.ReactNode} The SummaryWeek component.
 */
const SummaryWeekModule = ({
  authorizedHours,
  employmentType,
  deliveredHours,
  scheduledHours,
  isSmallScreen,
}) => {
  if (!scheduledHours) {
    return <EmptySummary />;
  }

  const minimumHours = getMinimumHours(employmentType);
  const billedHoursPercentage = authorizedHours ? (deliveredHours / authorizedHours) * 100 : 0;
  const showMinimumHoursText = minimumHours && scheduledHours;
  const remainingForMinimum = showMinimumHoursText ? minimumHours - scheduledHours : 0;

  return (
    <Paper component="section" elevation={2} sx={ModuleContainer}>
      <ModuleHeader
        Icon={Heartbeat}
        title="Summary this week"
        subtitle={
          showMinimumHoursText && (
            <>
              {remainingForMinimum > 0 ? (
                <span>
                  Schedule{" "}
                  <Typography component="em" variant="subtitle2" color={orange[600]}>
                    {remainingForMinimum} more hours
                  </Typography>{" "}
                  to hit your weekly minimum of {minimumHours} hours.
                </span>
              ) : (
                <span>You&apos;ve hit your weekly minimum of {minimumHours} hours</span>
              )}
            </>
          )
        }
      />

      <Stack direction={isSmallScreen ? "column" : "row"} spacing={7} mt={7} alignItems="center">
        <BilledHoursChart value={billedHoursPercentage} color={purple[800]} />

        {isSmallScreen && <SummaryWeekLegend />}

        <Box pt={1} width="100%">
          <Typography component="h3" variant="h2" fontFamily="TestTiemposText" fontWeight={500}>
            <span style={{ fontSize: "2rem" }}>{deliveredHours}</span> hours billed
          </Typography>

          <Typography component="h4" variant="body1" color="text.secondary">
            / {authorizedHours || 0} hours authorized
          </Typography>

          <Button
            variant="outlined"
            color="inherit"
            sx={{ mt: 7 }}
            href="/clinician-schedule"
            startIcon={<CalendarDots />}
          >
            View my Schedule
          </Button>
        </Box>
      </Stack>

      {!isSmallScreen && <SummaryWeekLegend />}
    </Paper>
  );
};

export default SummaryWeekModule;
